import { useState } from 'react'
import { Zap, TrendingDown, PieChart, AlertCircle, CheckCircle, Target, Lightbulb } from 'lucide-react'

export default function AIFinancialAssistPage() {
  const [currentAnalysis, setCurrentAnalysis] = useState({
    monthlyIncome: 5500,
    totalExpenses: 3200,
    savingsRate: 42,
  })

  const [savingsOpportunities, setSavingsOpportunities] = useState([
    { id: 1, category: 'Subscriptions', current: 85, potential: 45, savings: 40, recommendation: 'Cancel unused streaming services, consolidate to 1-2 main platforms' },
    { id: 2, category: 'Dining Out', current: 450, potential: 250, savings: 200, recommendation: 'Meal prep 2 days per week, reduce restaurant visits from 4x to 2x weekly' },
    { id: 3, category: 'Utilities', current: 180, potential: 120, savings: 60, recommendation: 'Switch to energy-efficient appliances, optimize thermostat settings' },
    { id: 4, category: 'Insurance', current: 320, potential: 220, savings: 100, recommendation: 'Shop insurance quotes annually, increase deductibles for lower premiums' },
    { id: 5, category: 'Transportation', current: 400, potential: 280, savings: 120, recommendation: 'Use public transit 2 days/week, carpool with colleagues' },
  ])

  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisComplete, setAnalysisComplete] = useState(false)

  const totalSavingsPotential = savingsOpportunities.reduce((sum, opp) => sum + opp.savings, 0)

  const analyzeExpenses = () => {
    setIsAnalyzing(true)
    setTimeout(() => {
      setAnalysisComplete(true)
      setIsAnalyzing(false)
    }, 2500)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">AI Financial Assistant</h1>
        <p className="text-blue-200">Discover ways to save money and optimize your finances</p>
      </div>

      {/* Financial Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-lg border border-green-500/30 p-6">
          <p className="text-green-300/70 text-sm mb-1">Monthly Income</p>
          <p className="text-3xl font-bold text-green-400">${currentAnalysis.monthlyIncome.toLocaleString()}</p>
          <p className="text-xs text-green-300 mt-2">Gross monthly income</p>
        </div>

        <div className="bg-gradient-to-br from-red-600/20 to-pink-600/20 rounded-lg border border-red-500/30 p-6">
          <p className="text-red-300/70 text-sm mb-1">Total Expenses</p>
          <p className="text-3xl font-bold text-red-400">${currentAnalysis.totalExpenses.toLocaleString()}</p>
          <p className="text-xs text-red-300 mt-2">Current monthly spending</p>
        </div>

        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
          <p className="text-blue-300/70 text-sm mb-1">Savings Rate</p>
          <p className="text-3xl font-bold text-blue-400">{currentAnalysis.savingsRate}%</p>
          <p className="text-xs text-blue-300 mt-2">${(currentAnalysis.monthlyIncome - currentAnalysis.totalExpenses).toLocaleString()}/month</p>
        </div>
      </div>

      {/* Savings Potential Summary */}
      <div className="bg-gradient-to-br from-purple-600/20 to-blue-600/20 rounded-lg border border-purple-500/30 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white">AI-Identified Savings Opportunities</h2>
            <p className="text-blue-200 mt-2">Review AI recommendations to optimize your monthly expenses</p>
          </div>
          <Lightbulb size={32} className="text-purple-400" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div className="bg-slate-800/50 rounded-lg p-4 border border-purple-500/20">
            <p className="text-purple-300/70 text-sm mb-2">Total Monthly Potential</p>
            <p className="text-3xl font-bold text-purple-400">${totalSavingsPotential}</p>
            <p className="text-xs text-purple-300 mt-2">By implementing all recommendations</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-4 border border-purple-500/20">
            <p className="text-purple-300/70 text-sm mb-2">Annual Savings</p>
            <p className="text-3xl font-bold text-purple-400">${(totalSavingsPotential * 12).toLocaleString()}</p>
            <p className="text-xs text-purple-300 mt-2">Year-round impact</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-4 border border-purple-500/20">
            <p className="text-purple-300/70 text-sm mb-2">Opportunities Found</p>
            <p className="text-3xl font-bold text-purple-400">{savingsOpportunities.length}</p>
            <p className="text-xs text-purple-300 mt-2">Categories to optimize</p>
          </div>
        </div>

        <button
          onClick={analyzeExpenses}
          disabled={isAnalyzing}
          className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          <Zap size={20} />
          <span>{isAnalyzing ? 'Analyzing Finances...' : 'Run AI Financial Analysis'}</span>
        </button>
      </div>

      {/* Savings Opportunities */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
          <TrendingDown size={24} />
          <span>Detailed Savings Opportunities</span>
        </h2>

        <div className="space-y-3">
          {savingsOpportunities.map((opp) => (
            <div key={opp.id} className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white">{opp.category}</h3>
                  <p className="text-sm text-blue-200 mt-1">{opp.recommendation}</p>
                </div>
                <div className="text-right ml-4">
                  <p className="text-2xl font-bold text-green-400">${opp.savings}</p>
                  <p className="text-xs text-blue-300">Monthly savings</p>
                </div>
              </div>

              <div className="flex items-center space-x-4 pt-3 border-t border-blue-500/20">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-blue-300">Current</span>
                    <span className="text-sm font-semibold text-white">${opp.current}</span>
                  </div>
                  <div className="w-full bg-slate-600/50 rounded-full h-2">
                    <div
                      className="bg-red-500/70 h-2 rounded-full"
                      style={{ width: '70%' }}
                    ></div>
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-blue-300">Optimized</span>
                    <span className="text-sm font-semibold text-green-400">${opp.potential}</span>
                  </div>
                  <div className="w-full bg-slate-600/50 rounded-full h-2">
                    <div
                      className="bg-green-500/70 h-2 rounded-full"
                      style={{ width: '40%' }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Budget Optimization Tips */}
      <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
          <Target size={24} />
          <span>Smart Financial Strategies</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
            <div className="flex items-start space-x-3">
              <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Automate Savings</p>
                <p className="text-sm text-blue-200 mt-1">Set up automatic transfers to savings account on payday</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
            <div className="flex items-start space-x-3">
              <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Track Expenses</p>
                <p className="text-sm text-blue-200 mt-1">Monitor spending weekly to catch unusual patterns early</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
            <div className="flex items-start space-x-3">
              <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Review Subscriptions</p>
                <p className="text-sm text-blue-200 mt-1">Audit all recurring charges monthly and cancel unused services</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
            <div className="flex items-start space-x-3">
              <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Build Emergency Fund</p>
                <p className="text-sm text-blue-200 mt-1">Aim for 3-6 months of expenses in liquid savings</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
            <div className="flex items-start space-x-3">
              <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Negotiate Bills</p>
                <p className="text-sm text-blue-200 mt-1">Shop around for insurance, internet, and phone annually</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
            <div className="flex items-start space-x-3">
              <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Use Budgeting Method</p>
                <p className="text-sm text-blue-200 mt-1">Try 50/30/20 rule: 50% needs, 30% wants, 20% savings</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-500/10 rounded-lg border border-yellow-500/30 p-6">
        <h3 className="text-lg font-bold text-yellow-300 mb-2 flex items-center space-x-2">
          <AlertCircle size={20} />
          <span>Important Disclaimer</span>
        </h3>
        <p className="text-yellow-200 text-sm">
          These recommendations are AI-generated suggestions based on general financial principles. For personalized financial advice, please consult with a qualified financial advisor. Individual circumstances vary, and these suggestions may not be appropriate for your specific situation.
        </p>
      </div>
    </div>
  )
}
